import DateBox from "devextreme-react/date-box";
import { confirm } from "devextreme/ui/dialog";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { downloadPhaseRequestResponseReport } from "../../../../../api/v2/FilesApi";
import { setPhaseActionById } from "../../../../../api/v2/PhasesApi";
import { getStep } from "../../../../../api/v2/StepsApi";
import { PhaseAction } from "../../../../../dtos/PhaseAction";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import { StepDto } from "../../../../../dtos/StepDto";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { useAppContext } from "../../../AppContext";
import { useTaskListContext } from "../../context/TaskListContext";

type IuseTaskActionsControllerProps = {
    initialPhase: PhaseDto | null;
    grantNumber: string;
    steps: StepDto[] | null;
    onSelected?: () => void;
};

export function useTaskActionsController({
    initialPhase,
    grantNumber,
    steps,
    onSelected,
}: IuseTaskActionsControllerProps) {
    const app = useAppContext();
    const taskCtx = useTaskListContext();

    const [phase, setPhase] = useState<PhaseDto | null>(initialPhase);
    const dueDateRef = useRef<DateBox>(null);

    useEffect(() => {
        setPhase(initialPhase);
    }, [initialPhase]);

    const effectiveMinDate = useMemo(() => {
        const today = new Date();
        const pd = phase?.dueDate ? new Date(phase.dueDate) : null;
        if (pd && pd > today) return pd;
        return today;
    }, [phase?.dueDate]);

    const workflowType = phase?.defaultPhase?.settings?.workflowType;
    const isStepWorkflow = workflowType === PhaseWorkflowType.Step;
    const isNoneWorkflow = workflowType === PhaseWorkflowType.None;

    // Hide task-level actions for Step and None workflows (they operate at subtask level only)
    const hideTaskActions = isStepWorkflow || isNoneWorkflow;

    const hasRelease = !hideTaskActions && !!phase?.phaseActions?.includes(PhaseAction.RELEASE);
    const hasSubmit = !hideTaskActions && !!phase?.phaseActions?.includes(PhaseAction.SUBMIT);
    const hasRequestRevision = !hideTaskActions && !!phase?.phaseActions?.includes(PhaseAction.REQUESTREVISION);
    const hasApprove = !hideTaskActions && !!phase?.phaseActions?.includes(PhaseAction.APPROVE);

    // Phase-specific behavior flags
    const CONSTRUCTION_PHASE_ID = 800; // CoreDefaultPhaseIds.DC_C
    const defaultPhaseId = phase?.defaultPhase?.id;
    const isConstructionPhase = defaultPhaseId === CONSTRUCTION_PHASE_ID;

    // Generate Report button visibility is now config-driven via phaseActions
    const reportButtonVisible = !!phase?.phaseActions?.includes(PhaseAction.GENERATE_REPORT);

    const downloadRequestResponseReport = useCallback(async () => {
        if (!phase) return;
        app.showLoadPanel();
        const result = await downloadPhaseRequestResponseReport(phase.id);
        if (result?.error) {
            app.errorToast(result.error.message);
        } else {
            const blob = result?.data;
            if (blob) {
                const link = document.createElement("a");
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank");
                link.href = url;
                link.download = `${phase.name}_${grantNumber}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 10_000);
            } else {
                app.errorToast("Failed to download");
            }
        }
        app.hideLoadPanel();
    }, [app, grantNumber, phase]);

    const onAction = useCallback(
        async (action: PhaseAction) => {
            if (!phase) return;

            const dueDate = dueDateRef.current?.instance.option("value") as Date | null | undefined;

            const payload: PhaseDto = {
                ...phase,
                dueDate: (dueDate ?? null),
                startDate: phase.startDate,
                projectedEndDate: phase.projectedEndDate,
            };

            const updatedPhase = await setPhaseActionById(phase.id, action, payload);
            if (updatedPhase?.error) {
                app.errorToast(updatedPhase.error.message || "There was a problem updating this phase action.");
                return;
            }

            const step = taskCtx.getSelectedStep();
            if (step) {
                const fetched = await getStep(step.id ?? "");
                if (fetched) {
                    if (!fetched.error) {
                        taskCtx.setSelectedStep(fetched.data);
                        await taskCtx.refreshSteps(fetched.data as StepDto);
                    } else {
                        app.errorToast(fetched.error.message);
                    }
                }
            } else {
                await taskCtx.refreshSteps();
            }

            if (updatedPhase?.data) setPhase(updatedPhase.data as PhaseDto);
            if (onSelected) onSelected();
        },
        [app, onSelected, phase, taskCtx]
    );

    const handleActionClick = useCallback(
        async (message: string | null, action: PhaseAction) => {
            let msg = message ?? "";

            if (action === PhaseAction.APPROVE) {
                const hasPending = steps?.some(s => s.status === StepStatus.GRANTEE_PENDING_REVISION);
                if (hasPending) {
                    msg += "<br/><br/><strong>WARNING: One or more subtasks is in a Revision state, this will mark them Approved</strong>";
                }
            }

            const result = await confirm(msg, "Confirm");
            if (result) await onAction(action);
        },
        [onAction, steps]
    );

    const onDueDateChanged = useCallback((val: Date | null) => {
        setPhase(prev => (prev ? { ...prev, dueDate: val } : prev));
    }, []);

    const onStartDateChanged = useCallback((val: Date | null) => {
        setPhase(prev => (prev ? { ...prev, startDate: val } : prev));
    }, []);

    const onProjectedEndDateChanged = useCallback((val: Date | null) => {
        setPhase(prev => (prev ? { ...prev, projectedEndDate: val } : prev));
    }, []);

    return {
        phase,
        dueDateRef,
        hasRelease,
        hasSubmit,
        hasRequestRevision,
        hasApprove,
        reportButtonVisible,
        effectiveMinDate,
        handleActionClick,
        downloadRequestResponseReport,
        onDueDateChanged,
        // Only provide date change handlers if NOT Construction phase
        onStartDateChanged: isConstructionPhase ? undefined : onStartDateChanged,
        onProjectedEndDateChanged: isConstructionPhase ? undefined : onProjectedEndDateChanged,
    };
}
